import React, { useId } from 'react';
import Dropdown from '../../../components/Dropdown/Dropdown';
import { useI18n } from '../../../i18n/I18nContext';
import { PROMO_REDEEMABLE } from '../reminderUi';

/**
 * The two optional extras of a message, each a Dropdown:
 *   - the course its {link} opens ("Avtomatik" = every person's own course, or the catalogue)
 *   - a promo code that rides along ("SALE10 · 10%")
 * A list the login cannot read (or that is empty) leaves its field out, so a form never offers
 * something the server would refuse. `courseId` / `promoId` are '' for none. The promo field is
 * left out altogether while PROMO_REDEEMABLE is false (checkout cannot redeem a code yet).
 *
 *   <LinkFields courses={options.courses} promos={options.promos}
 *               courseId={course} onCourseChange={setCourse} promoId={promo} onPromoChange={setPromo} />
 */
export default function LinkFields({
  courses = [],
  promos = [],
  courseId,
  onCourseChange,
  promoId,
  onPromoChange,
  className = '',
}) {
  const { t } = useI18n();
  const uid = useId();

  const showCourse = courses.length > 0;
  const showPromo = PROMO_REDEEMABLE && promos.length > 0;
  if (!showCourse && !showPromo) return null;

  const courseOptions = [
    { value: '', label: t('reminders.link.automatic') },
    ...courses.map((course) => ({ value: course.id, label: course.title })),
  ];
  // A code that was chosen earlier and no longer works stays visible, so the person can see and change it.
  const promoOptions = [
    { value: '', label: t('reminders.link.noPromo') },
    ...promos.map((promo) => ({ value: promo.id, label: promo.label })),
  ];
  if (promoId && !promos.some((promo) => promo.id === promoId)) {
    promoOptions.push({ value: promoId, label: t('reminders.link.promoGone') });
  }
  if (courseId && !courses.some((course) => course.id === courseId)) {
    courseOptions.push({ value: courseId, label: t('reminders.link.courseGone') });
  }

  return (
    <div className={`ui-form-grid ${className}`.trim()}>
      {showCourse && (
        <div className="ui-field">
          <span className="ui-label" id={`${uid}-course`}>{t('reminders.link.course')}</span>
          <Dropdown value={courseId || ''} onChange={onCourseChange} options={courseOptions} labelledBy={`${uid}-course`} />
          <p className="ui-hint">{t('reminders.link.courseHint')}</p>
        </div>
      )}
      {showPromo && (
        <div className="ui-field">
          <span className="ui-label" id={`${uid}-promo`}>{t('reminders.link.promo')}</span>
          <Dropdown value={promoId || ''} onChange={onPromoChange} options={promoOptions} labelledBy={`${uid}-promo`} />
          <p className="ui-hint">{t('reminders.link.promoHint')}</p>
        </div>
      )}
    </div>
  );
}
